// src/components/locations/cities/CitiesTable.tsx
import React from "react";
import { FiChevronLeft, FiChevronRight, FiEdit2, FiTrash2 } from "react-icons/fi";

import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import type { LocationCity, LocationRegion } from "@/api/features/locations/types";

export type CitiesTableFilters = {
  name?: string;
};

type CitiesTableProps = {
  rows?: LocationCity[];
  page: number;
  pageSize: number;
  total: number;
  isLoading?: boolean;
  filters: CitiesTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<CitiesTableFilters>>;
  onPageChange: (page: number) => void;
  onEdit: (row: LocationCity) => void;
  onDelete: (row: LocationCity) => void;
  regions: LocationRegion[];
};

const CitiesTable: React.FC<CitiesTableProps> = ({
  rows,
  page,
  pageSize,
  total,
  isLoading = false,
  filters,
  setFilters,
  onPageChange,
  onEdit,
  onDelete,
  regions,
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safePageSize = pageSize > 0 ? pageSize : 10;
  const safeTotal = total || 0;

  const totalPages = Math.max(1, Math.ceil(safeTotal / safePageSize));
  const startIndex = safeTotal === 0 ? 0 : (page - 1) * safePageSize;
  const endIndex = safeTotal === 0 ? 0 : Math.min(startIndex + safePageSize, safeTotal);

  const showSkeleton = isLoading && safeRows.length === 0;
  const showEmptyState = !isLoading && safeRows.length === 0;

  const handlePrev = () => {
    if (page > 1) onPageChange(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages) onPageChange(page + 1);
  };

  const regionNameById = React.useMemo(() => {
    const m = new Map<number, string>();
    for (const r of regions) m.set(r.id, r.name);
    return m;
  }, [regions]);

  const getRegionId = (row: LocationCity): number | null | undefined => {
    const anyRow = row as any;
    if (typeof anyRow.regionId === "number" || anyRow.regionId === null) return anyRow.regionId;
    if (typeof anyRow.region_id === "number" || anyRow.region_id === null) return anyRow.region_id;
    return undefined;
  };

  const regionLabel = (regionId: number | null | undefined) => {
    if (regionId === null) return "No region";
    if (typeof regionId !== "number") return "—";
    return regionNameById.get(regionId) ?? `#${regionId}`;
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Name</span>
                  <ColumnFilter label="Name">
                    <Input
                      placeholder="Contains…"
                      value={filters.name || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>
              <Th>Country</Th>
              <Th>Region</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {showSkeleton && (
              <tr>
                <Td colSpan={5}>
                  <div className="flex items-center justify-center py-10 text-xs text-gray-500">
                    Loading cities…
                  </div>
                </Td>
              </tr>
            )}

            {showEmptyState && (
              <tr>
                <Td colSpan={5}>
                  <div className="flex flex-col items-center justify-center py-10 text-sm text-gray-500">
                    <div>No cities found.</div>
                    <div className="mt-1 text-xs text-gray-400">Try adjusting your search or filters.</div>
                  </div>
                </Td>
              </tr>
            )}

            {!showSkeleton &&
              !showEmptyState &&
              safeRows.map((row, index) => {
                const rid = getRegionId(row);
                return (
                  <tr key={row.id} className="hover:bg-gray-50/60">
                    <Td className="text-gray-700">{startIndex + index + 1}</Td>
                    <Td className="text-gray-900">{row.name}</Td>
                    <Td className="text-gray-700 font-mono text-xs">{row.countryIso2}</Td>
                    <Td className="text-gray-700">{regionLabel(rid)}</Td>
                    <Td>
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                          onClick={() => onEdit(row)}
                          aria-label="Edit city"
                          title="Edit city"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                          onClick={() => onDelete(row)}
                          aria-label="Delete city"
                          title="Delete city"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <div className="md:hidden divide-y divide-gray-200">
        {showSkeleton && <div className="p-6 text-center text-xs text-gray-500">Loading cities…</div>}

        {showEmptyState && <div className="p-6 text-center text-sm text-gray-500">No cities found.</div>}

        {!showSkeleton &&
          !showEmptyState &&
          safeRows.map((row, index) => {
            const rid = getRegionId(row);
            return (
              <div key={row.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">
                      {startIndex + index + 1}. {row.name}
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                      <span className="rounded-md bg-gray-100 px-2 py-0.5 font-mono">
                        {row.countryIso2}
                      </span>
                      <span className="truncate">Region: {regionLabel(rid)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                      onClick={() => onEdit(row)}
                      aria-label="Edit city"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(row)}
                      aria-label="Delete city"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
        <div className="text-xs text-gray-500">
          Showing <span className="font-medium text-gray-700">{safeTotal === 0 ? 0 : startIndex + 1}</span> to{" "}
          <span className="font-medium text-gray-700">{endIndex}</span> of{" "}
          <span className="font-medium text-gray-700">{safeTotal}</span> results
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handlePrev}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiChevronLeft /> Prev
          </button>

          <div className="hidden sm:flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === page;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => onPageChange(n)}
                  className={`rounded-md px-2.5 py-1.5 text-sm ${
                    active ? "bg-sky-600 text-white" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && <span className="px-1 text-sm text-gray-500">…</span>}
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next <FiChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

type CellProps = {
  children: React.ReactNode;
  className?: string;
  colSpan?: number;
};

function Th({ children, className = "" }: CellProps) {
  return <th className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}>{children}</th>;
}

function Td({ children, className = "", colSpan }: CellProps) {
  return (
    <td className={`px-5 py-3.5 ${className}`} colSpan={colSpan}>
      {children}
    </td>
  );
}

export default CitiesTable;
